import React, { useState, useEffect } from 'react';
import { Table, Button, Input, Select, Modal, Form, notification, Spin, DatePicker, Tabs } from 'antd';
import { httpGet, httpPost, customBaseUrl } from '../../store/http';
import { showLoader, hideLoader } from '../../utils/loader';
import Layout from "../../components/Layout";
import moment from 'moment';

const { Option } = Select;
const { RangePicker } = DatePicker;
const { TabPane } = Tabs;

const NipInwardReconciliation = () => {
  const adminEmail = JSON.parse(localStorage.getItem("userDetails"))?.user?.email || 'admin@example.com'; // Fallback for safety
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0,
  });
  const [filters, setFilters] = useState({
    sessionId: '',
    status: '',
    startDate: null,
    endDate: null,
  });
  const [isTransactionDetailModalVisible, setIsTransactionDetailModalVisible] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [isReconciliationModalVisible, setIsReconciliationModalVisible] = useState(false);
  const [reconciliationForm] = Form.useForm();
  const [pendingReconciliations, setPendingReconciliations] = useState([]);
  const [loadingPending, setLoadingPending] = useState(false);

  const fetchNipTransactions = async (page, size, currentFilters) => {
    setLoading(true);
    try {
      const params = {
        page: page - 1, // API is 0-indexed
        size: size,
        sortBy: 'createdAt',
        sortDirection: 'DESC',
        ...currentFilters,
        startDate: currentFilters.startDate ? moment(currentFilters.startDate).format('YYYY-MM-DD') : undefined,
        endDate: currentFilters.endDate ? moment(currentFilters.endDate).format('YYYY-MM-DD') : undefined,
      };

      const queryString = new URLSearchParams(params).toString();
      const res = await httpGet(`${customBaseUrl.tranv2}/api/v1/nip-inward-reconciliation/transactions?${queryString}`);

      if (res.success || res.status === true) {
        setTransactions(res.data.content || []);
        setPagination((prev) => ({
          ...prev,
          total: res.data.totalElements,
          current: res.data.number + 1,
          pageSize: res.data.size,
        }));
      } else {
        notification.error({ message: res.message || 'Failed to fetch NIP transactions.' });
      }
    } catch (error) {
      notification.error({ message: error.message || 'An error occurred while fetching NIP transactions.' });
    } finally {
      setLoading(false);
    }
  };

  const fetchNipTransactionById = async (sessionId) => {
    showLoader();
    try {
      const res = await httpGet(`${customBaseUrl.tranv2}/api/v1/nip-inward-reconciliation/transactions/${sessionId}`);
      if (res.success || res.status === true) {
        setSelectedTransaction(res.data);
        setIsTransactionDetailModalVisible(true);
      } else {
        notification.error({ message: res.message || 'Failed to fetch transaction details.' });
      }
    } catch (error) {
      notification.error({ message: error.message || 'An error occurred while fetching transaction details.' });
    } finally {
      hideLoader();
    }
  };

  const fetchPendingManualReconciliations = async () => {
    setLoadingPending(true);
    try {
      const res = await httpGet(`${customBaseUrl.tranv2}/api/v1/nip-inward-reconciliation/reconciliation/admin/pending-manual-reconciliation`);
      if (res.success || res.status === true) {
        setPendingReconciliations(res.data.content || []);
      } else {
        notification.error({ message: res.message || 'Failed to fetch pending reconciliations.' });
      }
    } catch (error) {
      notification.error({ message: error.message || 'An error occurred while fetching pending reconciliations.' });
    } finally {
      setLoadingPending(false);
    }
  };

  const handleConfirmAndReconcile = async (values) => {
    showLoader();
    try {
      // Manual check for confirmedResponseCode
      if (!values.confirmedResponseCode) {
        notification.error({ message: 'Confirmed Response Code is required for reconciliation.' });
        hideLoader();
        return;
      }

      const payload = {
        transactionId: selectedTransaction.id, // Assuming selectedTransaction has an id
        sessionId: selectedTransaction.sessionId,
        confirmedBy: adminEmail, // Use adminEmail directly
        confirmedResponseCode: values.confirmedResponseCode,
        notes: values.notes,
      };
      const res = await httpPost(`${customBaseUrl.tranv2}/api/v1/nip-inward-reconciliation/reconciliation/admin/confirm-and-reconcile`, payload);
      if (res.success || res.status === true) {
        notification.success({ message: 'Transaction confirmed and reconciled successfully!' });
        setIsReconciliationModalVisible(false);
        reconciliationForm.resetFields();
        fetchNipTransactions(pagination.current, pagination.pageSize, filters); // Refresh list
        fetchPendingManualReconciliations(); // Refresh pending list
      } else {
        notification.error({ message: res.message || 'Failed to confirm and reconcile transaction.' });
      }
    } catch (error) {
      notification.error({ message: error.message || 'An error occurred during reconciliation.' });
    } finally {
      hideLoader();
    }
  };

  const handleCheckWithNibss = async (values) => {
    showLoader();
    try {
      const payload = {
        transactionId: selectedTransaction.id, // Assuming selectedTransaction has an id
        sessionId: selectedTransaction.sessionId,
        checkedBy: adminEmail, // Use adminEmail directly
        notes: values.notes,
      };
      const res = await httpPost(`${customBaseUrl.tranv2}/api/v1/nip-inward-reconciliation/reconciliation/admin/check-nibss`, payload);
      if (res.success || res.status === true) {
        notification.success({ message: 'Transaction checked with NIBSS successfully!' });
        // Assuming res.data contains the response code from NIBSS
        const nibssResponseCode = selectedTransaction?.responseCode ?? res.data?.responseCode ?? '00';
        reconciliationForm.setFieldsValue({ confirmedResponseCode: nibssResponseCode });

        // Re-fetch the selected transaction to update its status in the modal
        const updatedTransactionRes = await httpGet(`${customBaseUrl.tranv2}/api/v1/nip-inward-reconciliation/transactions/${selectedTransaction.sessionId}`);
        if (updatedTransactionRes.success || updatedTransactionRes.status === true) {
          setSelectedTransaction(updatedTransactionRes.data);
        } else {
          notification.warn({ message: 'Failed to refresh transaction status after NIBSS check.' });
        }

        fetchNipTransactions(pagination.current, pagination.pageSize, filters); // Refresh list
        fetchPendingManualReconciliations(); // Refresh pending list
      } else {
        notification.error({ message: res.message || 'Failed to check transaction with NIBSS.' });
      }
    } catch (error) {
      notification.error({ message: error.message || 'An error occurred while checking with NIBSS.' });
    } finally {
      hideLoader();
    }
  };

  useEffect(() => {
    fetchNipTransactions(pagination.current, pagination.pageSize, filters);
    fetchPendingManualReconciliations();
  }, [pagination.current, pagination.pageSize, filters]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleTableChange = (newPagination) => {
    setPagination((prev) => ({
      ...prev,
      current: newPagination.current,
      pageSize: newPagination.pageSize,
    }));
  };

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const handleDateRangeChange = (dates) => {
    setFilters((prev) => ({
      ...prev,
      startDate: dates ? dates[0] : null,
      endDate: dates ? dates[1] : null,
    }));
  };

  const handleSearch = () => {
    setPagination((prev) => ({ ...prev, current: 1 })); // Reset to first page on new search
    fetchNipTransactions(1, pagination.pageSize, filters);
  };

  const columns = [
    { title: 'Session ID', dataIndex: 'sessionId', key: 'sessionId' },
    { title: 'Amount', dataIndex: 'amount', key: 'amount' },
    { title: 'Originator Account', dataIndex: 'originatorAccount', key: 'originatorAccount' },
    { title: 'Originator Bank', dataIndex: 'originatorBankName', key: 'originatorBankName' },
    { title: 'Beneficiary Account', dataIndex: 'beneficiaryAccount', key: 'beneficiaryAccount' },
    { title: 'Beneficiary Name', dataIndex: 'beneficiaryAccountName', key: 'beneficiaryAccountName' },
    { title: 'Status', dataIndex: 'status', key: 'status' },
    { title: 'Reconciliation Status', dataIndex: 'reconciliationStatus', key: 'reconciliationStatus' },
    { title: 'Created At', dataIndex: 'createdAt', key: 'createdAt', render: (text) => moment(text).format('YYYY-MM-DD HH:mm:ss') },
    {
      title: 'Action',
      key: 'action',
      render: (_, record) => (
        <>
          <Button type="link" onClick={() => fetchNipTransactionById(record.sessionId)}>
            View Details
          </Button>
          {record.reconciliationStatus === 'UNRECONCILED' && (
            <Button
              type="link"
              onClick={() => {
                setSelectedTransaction(record);
                setIsReconciliationModalVisible(true);
              }}
            >
              Reconcile
            </Button>
          )}
        </>
      ),
    },
  ];

  const pendingColumns = [
    { title: 'Session ID', dataIndex: 'sessionId', key: 'sessionId' },
    { title: 'Amount', dataIndex: 'amount', key: 'amount' },
    { title: 'Status', dataIndex: 'status', key: 'status' },
    {
      title: 'Action',
      key: 'action',
      render: (_, record) => (
        <Button type="link" onClick={() => {
          setSelectedTransaction(record);
          setIsReconciliationModalVisible(true);
        }}>
          Reconcile
        </Button>
      ),
    },
  ];

  return (
    <Layout>
      <h1>NIP Inward Reconciliation</h1>

      <Tabs defaultActiveKey="1">
        <TabPane tab="All NIP Transactions" key="1">
          {/* Filters */}
          <div style={{ marginBottom: 16, display: 'flex', gap: 16 }}>
            <Input
              placeholder="Session ID"
              value={filters.sessionId}
              onChange={(e) => handleFilterChange('sessionId', e.target.value)}
              style={{ width: 200 }}
            />
            <Select
              placeholder="Status"
              style={{ width: 150 }}
              value={filters.status}
              onChange={(value) => handleFilterChange('status', value)}
              allowClear
            >
              <Option value="">ALL</Option>
              <Option value="TSQ_FAILED">TSQ FAILED</Option>
              <Option value="CREDIT_ERROR">CREDIT ERROR</Option>
              <Option value="PENDING_MANUAL_RECONCILIATION">PENDING MANUAL RECONCILIATION</Option>
              <Option value="CREDITED">CREDITED</Option>
              <Option value="ADMIN_CHECKING">ADMIN CHECKING</Option>

              {/* Add other relevant statuses */}
            </Select>
            <RangePicker
              value={[filters.startDate, filters.endDate]}
              onChange={handleDateRangeChange}
            />
            <Button type="primary" onClick={handleSearch}>
              Search
            </Button>
          </div>

          {/* NIP Transactions Table */}
          <Table
            columns={columns}
            dataSource={transactions}
            rowKey="sessionId"
            pagination={pagination}
            loading={loading}
            onChange={handleTableChange}
            scroll={{ x: 'max-content' }}
          />
        </TabPane>
        <TabPane tab="Pending Manual Reconciliations" key="2">
          {/* Pending Manual Reconciliations */}
          <Table
            columns={pendingColumns}
            dataSource={pendingReconciliations}
            rowKey="sessionId"
            pagination={false} // No pagination for this small list
            loading={loadingPending}
            scroll={{ x: 'max-content' }}
          />
        </TabPane>
      </Tabs>

      {/* Transaction Details Modal */}
      <Modal
        title="Transaction Details"
        visible={isTransactionDetailModalVisible}
        onCancel={() => setIsTransactionDetailModalVisible(false)}
        footer={null}
        width={700}
      >
        {selectedTransaction ? (
          <Spin spinning={loading}>
            <p><strong>Session ID:</strong> {selectedTransaction.sessionId}</p>
            <p><strong>Transaction Type:</strong> {selectedTransaction.transactionType}</p>
            <p><strong>Response Code:</strong> {selectedTransaction.responseCode}</p>
            <p><strong>Amount:</strong> {selectedTransaction.amount}</p>
            <p><strong>Originator Account:</strong> {selectedTransaction.originatorAccount}</p>
            <p><strong>Originator Bank:</strong> {selectedTransaction.originatorBankName}</p>
            <p><strong>Beneficiary Account:</strong> {selectedTransaction.beneficiaryAccount}</p>
            <p><strong>Beneficiary Name:</strong> {selectedTransaction.beneficiaryAccountName}</p>
            <p><strong>Status:</strong> {selectedTransaction.status}</p>
            <p><strong>Reconciliation Status:</strong> {selectedTransaction.reconciliationStatus}</p>
            <p><strong>TSQ Retry Count:</strong> {selectedTransaction.tsqRetryCount}</p>
            <p><strong>Last TSQ Response Code:</strong> {selectedTransaction.lastTsqResponseCode}</p>
            <p><strong>First TSQ Attempt At:</strong> {moment(selectedTransaction.firstTsqAttemptAt).format('YYYY-MM-DD HH:mm:ss')}</p>
            <p><strong>Created At:</strong> {moment(selectedTransaction.createdAt).format('YYYY-MM-DD HH:mm:ss')}</p>
            <p><strong>Updated At:</strong> {moment(selectedTransaction.updatedAt).format('YYYY-MM-DD HH:mm:ss')}</p>
            <p><strong>Processed At:</strong> {moment(selectedTransaction.processedAt).format('YYYY-MM-DD HH:mm:ss')}</p>
          </Spin>
        ) : (
          <p>No transaction selected.</p>
        )}
      </Modal>

      {/* Reconciliation Modal */}
      <Modal
        title="Reconcile Transaction"
        visible={isReconciliationModalVisible}
        onCancel={() => setIsReconciliationModalVisible(false)}
        footer={null}
        destroyOnClose
      >
        {selectedTransaction && (
          <Form form={reconciliationForm} layout="vertical">
            <p><strong>Session ID:</strong> {selectedTransaction.sessionId}</p>
            <p><strong>Amount:</strong> {selectedTransaction.amount}</p>
            <Form.Item
              name="confirmedResponseCode"
              label="Confirmed Response Code"
            >
              <Input />
            </Form.Item>
            <Form.Item
              name="notes"
              label="Notes"
            >
              <Input.TextArea />
            </Form.Item>
            <Form.Item>
              <Button
                type="primary"
                onClick={() => reconciliationForm.validateFields().then(handleConfirmAndReconcile)}
                disabled={selectedTransaction?.status !== 'ADMIN_CHECKING'} // Disable if not ADMIN_CHECKING
              >
                Confirm and Reconcile
              </Button>
              <Button style={{ marginLeft: 8 }} onClick={() => reconciliationForm.validateFields().then(handleCheckWithNibss)}>
                Check with NIBSS
              </Button>
            </Form.Item>
          </Form>
        )}
      </Modal>
    </Layout>
  );
};

export default NipInwardReconciliation;